import React from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom'

// UPDATED: added .jsx extensions to all component imports
import Login from './components/auth/Login.jsx'
import Register from './components/auth/Register.jsx'
import UploadIndicator from './components/common/UploadIndicator.jsx'
import AdminDashboard from './components/dashboard/AdminDashboard.jsx'
import FunderDashboard from './components/dashboard/FunderDashboard.jsx'
import ProjectManagerDashboard from './components/dashboard/ProjectManagerDashboard.jsx'
import FinanceDashboard from './components/dashboard/FinanceDashboard.jsx'
import ConsultantDashboard from './components/dashboard/ConsultantDashboard.jsx'
import HRDashboard from './components/dashboard/HRDashboard.jsx'
import Launchpad from './components/platform/Launchpad.jsx'
import PlatformAdmin from './components/platform/PlatformAdmin.jsx'
import MyOrganisation from './components/organisation/MyOrganisation.jsx'
import TemplateLibrary from './components/templates/TemplateLibrary.jsx'
import RecycleBin from './components/admin/RecycleBin.jsx'
import FileManager from './components/admin/FileManager.jsx'
import ArchiveOverview from './components/admin/ArchiveOverview.jsx'
import MyTickets from './components/common/MyTickets.jsx'
import ProductDashboard from './components/platform/ProductDashboard.jsx'
import SsoLanding from './components/platform/SsoLanding.jsx'
import ProductRooms from './components/platform/ProductRooms.jsx'
import ProductRoom from './components/platform/ProductRoom.jsx'
import MyLeave from './components/hr/MyLeave.jsx'
import HRPeopleOps from './components/hr/HRPeopleOps.jsx'
import MobileShell from './components/mobile/MobileShell.jsx'
import OrgSsoLanding from './components/auth/OrgSsoLanding.jsx'
import ProjectList from './components/projects/ProjectList.jsx'
import ProjectDetails from './components/projects/ProjectDetails.jsx'
import KanbanPage from './components/projects/KanbanPage.jsx'
import FundingApproval from './components/funding/FundingApproval.jsx'
import BudgetAllocation from './components/funding/BudgetAllocation.jsx'
import FinancialReports from './components/finance/FinancialReports.jsx'
import BudgetTracking from './components/finance/BudgetTracking.jsx'
import ExpenseManagement from './components/finance/ExpenseManagement.jsx'
import MyTeam from './components/team/MyTeam.jsx'
import Teams, { TeamPage } from './components/teams/Teams.jsx'
import Approvals from './components/approvals/Approvals.jsx'
import CompanyHome from './components/company/CompanyHome.jsx'
import CompanyArchive from './components/company/CompanyArchive.jsx'
import UserManagement from './components/team/UserManagement.jsx'
import ConsultantProjects from './components/consultant/ConsultantProjects.jsx'
import Recommendations from './components/consultant/Recommendations.jsx'
import Navigation from './components/layout/Navigation.jsx'
import Sidebar from './components/layout/Sidebar.jsx'

// --- NEW: Import the ActionBar ---
import ActionBar from './components/layout/ActionBar.jsx'

// --- NEW: Import the Consultants Page ---
import ConsultantsPage from './components/consultant/ConsultantsPage.jsx'

// V2: Onboarding + TOR
import OnboardingPipeline from './components/onboarding/OnboardingPipeline.jsx'
import PeopleManagement from './components/admin/PeopleManagement.jsx'
import PersonalDataExport from './components/admin/PersonalDataExport.jsx'
import FeedbackInbox from './components/admin/FeedbackInbox.jsx'
import MyOnboarding from './components/onboarding/MyOnboarding.jsx'
import TORUploadPage from './components/projects/TORUploadPage.jsx'
import ConsultantInvitePage from './components/onboarding/ConsultantInvitePage.jsx'
import ComplaintForm from './components/legal/ComplaintForm.jsx'
import RegisterComplaints from './components/legal/RegisterComplaints.jsx'
import GiveAccess from './components/legal/GiveAccess.jsx'

// V2: Contracts
import ContractList from './components/contracts/ContractList.jsx'
import ContractDetail from './components/contracts/ContractDetail.jsx'

// V2: Finance invoice queue
import InvoiceQueue from './components/finance/InvoiceQueue.jsx'

// V2: Project closure + document repository
import ProjectClosure from './components/projects/ProjectClosure.jsx'
import RedirectToDocumentsTab from './components/projects/RedirectToDocumentsTab.jsx'

// V2: Disputes
import DisputesList from './components/disputes/DisputesList.jsx'
import DemoReset from './components/admin/DemoReset.jsx'

// --- NEW: Import Profile & Preferences ---
import ProfileSettings from './components/team/ProfileSettings.jsx'
import Preferences from './components/team/Preferences.jsx'
import RolesGuide from './components/help/RolesGuide.jsx'
import RoleGuide from './components/help/RoleGuide.jsx'
// ------------------------------------

// UPDATED: added correct .js and .jsx extensions
import { useAuth } from './hooks/useAuth.js' // Assuming useAuth.js is correct
import { userRoles } from './data/mockData.js' // Assuming mockData.js is correct
import { ProjectsProvider } from './hooks/useProjects.jsx'
import { NotificationProvider } from './hooks/useNotifications.jsx'
import { SidebarProvider } from './hooks/useSidebar.jsx'
import { ProductContextProvider } from './hooks/useProductContext.jsx'
import { ThemeProvider } from './hooks/useTheme.jsx'
import i18n from './i18n.js'

// Simple placeholder component definition (needs to be inside or imported)
const placeholder = ({ title }) => (
  <div className="p-6">
    <h1 className="text-2xl font-bold">{title}</h1>
    <p className="mt-2 text-gray-600">{i18n.t('ui:under_construction')}</p>
  </div>
);

function AppContent() {
  const { isAuthenticated, user, loading } = useAuth()
  const location = useLocation()

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{i18n.t('ui:loading')}</p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    // Deep-linked emails (e.g. "you're assigned to a project" -> /projects/42?milestone=7)
    // land here when the user isn't logged in yet. Remember exactly where they were headed
    // so Login.jsx can send them straight there after they sign in, instead of stranding
    // them on the generic dashboard. Only remember paths that actually FALL THROUGH to
    // Login (path === '*' below) — not the public/token routes (never hit Login) and not
    // "/", "/login", or "/register" themselves, which aren't a deep-link destination.
    const path = location.pathname + location.search
    const isPublicRoute = /^\/(projects\/invite|consultant-invite|complaint|sso)\//.test(location.pathname)
    const isNonDestination = ['/', '/login', '/register'].includes(location.pathname)
    if (!isPublicRoute && !isNonDestination) {
      sessionStorage.setItem('postLoginRedirect', path)
    }
    return (
      <Routes>
        <Route path="/register" element={<Register />} />
        {/* Public TOR invite link — accessible without login */}
        <Route path="/projects/invite/:token" element={<TORUploadPage useToken={true} />} />
        {/* Public consultant invite link — Admin-generated, no login required */}
        <Route path="/consultant-invite/:token" element={<ConsultantInvitePage />} />
        {/* Public complaint form — Admin-generated link, no login required */}
        <Route path="/complaint/:token" element={<ComplaintForm />} />
        {/* Arriving from another product's button. The signed token IS the login. */}
        <Route path="/sso/organisation" element={<OrgSsoLanding />} />
        <Route path="/sso/:productKey" element={<SsoLanding />} />
        <Route path="*" element={<Login />} />
      </Routes>
    )
  }

  const getDashboardByRole = () => {
    // Inside a company: its own front page, with its numbers. Its Viewers go straight to their teams.
    if (user?.organisation && user?.role !== userRoles.PARTNER) return <CompanyHome />
    switch (user?.role) {
      case userRoles.ADMIN:
        return <AdminDashboard />
      case userRoles.Funder:
        return <FunderDashboard />
      case userRoles.PROJECT_MANAGER:
        return <ProjectManagerDashboard />
      case userRoles.FINANCE:
        return <FinanceDashboard />
      case userRoles.CONSULTANT:
        return <ConsultantDashboard />
      case userRoles.HR:
        return <HRDashboard />
      case userRoles.PARTNER:
        // Inside a company a Partner is its Viewer, and their work is the dossiers of their teams.
        if (user?.organisation) return <Navigate to="/teams" replace />
        // An external partner has nothing to see inside ProTrack — their work is in the product
        // they were given. The Launchpad is their home.
        return <Launchpad />
      default:
        // Deliberately NOT the Admin dashboard. An unrecognised role used to land there, which
        // meant any new role shipped straight into the admin UI until someone noticed. The
        // Launchpad is the safe landing: it shows only what this person actually has.
        return <Launchpad />
    }
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Navigation />

        {/* --- NEW: Add the ActionBar right below the Navigation --- */}
        <ActionBar />

        <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-50">
          <Routes>
            {/* v3.4 (F3): on a phone-sized screen, land in the chat-first mobile shell; desktop keeps the dashboard. */}
            <Route path="/" element={<Navigate to={(typeof window !== 'undefined' && window.innerWidth < 768) ? '/m' : '/dashboard'} replace />} />
            <Route path="/m" element={<MobileShell />} />
            <Route path="/dashboard" element={getDashboardByRole()} />
            <Route path="/projects" element={<ProjectList />} />
            <Route path="/projects/:id" element={<ProjectDetails />} />
            <Route path="/projects/:id/kanban" element={<KanbanPage />} />
            <Route path="/kanban" element={<KanbanPage />} />
            {/* Funding Requests removed (Mam #21) — redirect any old link to the dashboard */}
            <Route path="/funding/requests" element={<Navigate to="/dashboard" replace />} />
            <Route path="/funding/approval" element={<FundingApproval />} />
            <Route path="/funding/allocation" element={<BudgetAllocation />} />
            <Route path="/finance/reports" element={<FinancialReports />} />
            <Route path="/finance/budget" element={<BudgetTracking />} />
            <Route path="/finance/expenses" element={<ExpenseManagement />} />
            {/* Old demo team page retired — real pages are My Team + User Management */}
            <Route path="/team" element={<Navigate to="/my-team" replace />} />
            <Route path="/my-team" element={<MyTeam />} />
            <Route path="/teams" element={<Teams />} />
            <Route path="/teams/:id" element={<TeamPage />} />
            <Route path="/approvals" element={<Approvals />} />
            <Route path="/archive-retention" element={<CompanyArchive />} />
            <Route path="/team/management" element={<UserManagement />} />
            <Route path="/admin/people" element={<PeopleManagement />} />
            <Route path="/admin/personal-data-export" element={<PersonalDataExport />} />
            <Route path="/hr" element={<HRDashboard />} />
            <Route path="/hr/people-ops" element={<HRPeopleOps />} />
            <Route path="/hr/my-leave" element={<MyLeave />} />
            <Route path="/admin/feedback" element={<FeedbackInbox />} />
            {/* 3 Pillars ID — one login, many products. The Launchpad is for everyone; the
                platform panel guards itself server-side (Super Admin / manage_users). */}
            <Route path="/products" element={<Launchpad />} />
            {/* A product's own screens, drawn inside ProTrack from data it supplies. */}
            <Route path="/products/:productKey" element={<ProductDashboard />} />
            {/* A product's document rooms — one per country, scoped by the geography that
                product asserts about each person. */}
            <Route path="/products/:productKey/rooms" element={<ProductRooms />} />
            <Route path="/products/:productKey/rooms/:countryIso" element={<ProductRoom />} />
            {/* Also reachable while signed in — arriving with a product's token replaces the
                current session rather than silently ignoring the link. */}
            <Route path="/sso/organisation" element={<OrgSsoLanding />} />
            <Route path="/sso/:productKey" element={<SsoLanding />} />
            <Route path="/platform" element={<PlatformAdmin />} />
            {/* A customer's own administrator, running their own company. */}
            <Route path="/my-organisation" element={<MyOrganisation />} />
            {/* The shapes a new project can start from. */}
            <Route path="/templates" element={<TemplateLibrary />} />
            <Route path="/admin/recycle-bin" element={<RecycleBin />} />
            <Route path="/admin/file-manager" element={<FileManager />} />
            <Route path="/admin/archive" element={<ArchiveOverview />} />
            <Route path="/my-tickets" element={<MyTickets />} />
            <Route path="/consultant/projects" element={<ConsultantProjects />} />
            <Route path="/consultant/recommendations" element={<Recommendations />} />
            <Route path="/profile-settings" element={<ProfileSettings />} />
            <Route path="/preferences" element={<Preferences />} />
            <Route path="/roles" element={<RolesGuide />} />
            <Route path="/roles/guide" element={<RoleGuide />} />

            {/* --- NEW: Add the Consultants Route --- */}
            <Route path="/consultants" element={<ConsultantsPage />} />

            {/* V2: Onboarding routes */}
            <Route path="/onboarding" element={<OnboardingPipeline />} />
            <Route path="/onboarding/my" element={<MyOnboarding />} />

            {/* V2: Contract routes */}
            <Route path="/contracts" element={<ContractList />} />
            <Route path="/contracts/:id" element={<ContractDetail />} />

            {/* V2: Finance invoice queue */}
            <Route path="/finance/invoices" element={<InvoiceQueue />} />

            {/* V2: Project closure + document repository */}
            <Route path="/projects/:id/closure" element={<ProjectClosure />} />
            <Route path="/projects/:id/documents" element={<RedirectToDocumentsTab />} />

            {/* V2: Disputes */}
            <Route path="/disputes" element={<DisputesList />} />

            {/* Legal section */}
            <Route path="/legal/complaints" element={<RegisterComplaints />} />
            <Route path="/legal/access" element={<GiveAccess />} />
            {/* Complaint form also reachable while logged in (e.g. Admin previewing) */}
            <Route path="/complaint/:token" element={<ComplaintForm />} />

            {/* V2: Demo / Testing (Admin-only, gated by backend DEMO_MODE env var) */}
            <Route path="/demo" element={<DemoReset />} />

            {/* V2: TOR upload (logged-in PM) */}
            <Route path="/projects/from-tor" element={<TORUploadPage useToken={false} />} />

            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  )
}

function App() {
  return (
    <ThemeProvider>
      {/* Any upload, anywhere, signed in or not — an upload with no feedback looks like a hang,
          and the reasonable response to a hang is to refresh. */}
      <UploadIndicator />
      <Router>
        <ProjectsProvider>
          <NotificationProvider>
            <SidebarProvider>
              <ProductContextProvider>
                <AppContent />
              </ProductContextProvider>
            </SidebarProvider>
          </NotificationProvider>
        </ProjectsProvider>
      </Router>
    </ThemeProvider>
  )
}

export default App
